import {
    AfterViewInit,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    input,
    OnChanges,
    output,
    signal,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Panel, PanelContent, PanelHeader } from '../panel/panel.component';
import { NgIf } from '@angular/common';
import { Button } from '../button/button.component';
import { Icon } from '../icon/icon.component';
import { ToastService } from '../toast/toast.service';
import {
    Breadcrumbs,
    BreadcrumbItem,
} from '../breadcrumbs/breadcrumbs.component';

@Component({
    selector: 'ai-crud-header',
    standalone: true,
    template: ` <ng-content></ng-content>`,
})
export class CrudHeader {}

/**
 * Container para telas de formulário (criar/editar).
 *
 * Envolve um `FormGroup` num painel com breadcrumbs, título e ações de
 * salvar/cancelar. Ao submeter: se o formulário é válido, desabilita-o e emite
 * {@link submit} com os valores; se inválido, marca os campos como tocados e
 * exibe um toast de erro.
 */
@Component({
    selector: 'ai-crud',
    standalone: true,
    imports: [
        FormsModule,
        Button,
        Breadcrumbs,
        Icon,
        ReactiveFormsModule,
        Panel,
        PanelHeader,
        PanelContent,
        NgIf,
    ],
    templateUrl: './crud.component.html',
    styleUrls: ['crud.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
})
export class Crud implements OnChanges, AfterViewInit {
    private readonly destroyRef = inject(DestroyRef);

    /**
     * Sentinela posicionada logo após o rodapé de ações, no fim do formulário.
     * Quando ela sai da viewport, o rodapé está "flutuando" (colado ao fundo,
     * fora da sua posição de repouso) e ganha borda em todos os lados.
     */
    @ViewChild('actionsSentinel')
    private readonly actionsSentinel?: ElementRef<HTMLElement>;

    /** `true` enquanto o rodapé de ações está flutuante (sticky ativo). */
    protected readonly isActionsFloating = signal<boolean>(false);

    /** Formulário gerenciado pela tela (obrigatório). */
    public readonly formGroup = input.required<FormGroup>();
    /** Título da tela. */
    public readonly title = input<string>('CRUD');
    /** Ícone exibido junto ao título. */
    public readonly titleIcon = input<string | null>(null);
    /** Emite os valores do formulário ao submeter (quando válido). */
    public readonly submit = output<any>();
    /** Emite ao cancelar. */
    public readonly cancel = output<void>();
    /** Indica atividade de rede (ex.: salvando) para desabilitar ações. */
    public readonly isNetworkActive = input<boolean>(false);
    /** Itens da trilha de navegação (breadcrumbs). */
    public readonly breadcrumbItems = input<BreadcrumbItem[]>([]);
    /** Rótulo do botão de submissão (padrão `Confirmar`). */
    public readonly submitLabel = input<string>('Confirmar');
    /** Ícone (Font Awesome) do botão de submissão (padrão `fa-solid fa-check`). */
    public readonly submitIcon = input<string>('fa-solid fa-check');
    /**
     * Rótulo do indicador exibido no rodapé enquanto o formulário está "dirty"
     * (há alterações não salvas). Padrão `Formulário modificado`.
     */
    public readonly unsavedLabel = input<string>('Formulário modificado');
    /**
     * Rótulo do indicador exibido no rodapé enquanto o formulário está
     * "pristine" (sem alterações pendentes). Padrão `Formulário sem alterações`.
     */
    public readonly savedLabel = input<string>('Formulário sem alterações');
    /**
     * Rótulo do botão secundário quando o formulário tem alterações não salvas
     * (dirty) — descarta as mudanças. Padrão `Cancelar`.
     */
    public readonly cancelLabel = input<string>('Cancelar');
    /**
     * Rótulo do botão secundário quando o formulário está sem alterações
     * (pristine) — apenas volta à tela anterior. Padrão `Voltar`.
     */
    public readonly backLabel = input<string>('Voltar');

    constructor(
        private toastService: ToastService,
    ) {}

    public ngAfterViewInit(): void {
        // Detecta o estado "flutuante" do rodapé observando a sentinela no fim
        // do formulário: fora da viewport ⇒ há conteúdo abaixo ⇒ o rodapé está
        // colado ao fundo (fora do repouso). Sem suporte a IntersectionObserver
        // (ambientes não-browser), o rodapé permanece no estilo de repouso.
        const sentinel = this.actionsSentinel?.nativeElement;
        if (!sentinel || typeof IntersectionObserver === 'undefined') {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                this.isActionsFloating.set(!entries[0].isIntersecting);
            },
            { root: null, threshold: 0 },
        );
        observer.observe(sentinel);
        this.destroyRef.onDestroy(() => observer.disconnect());
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes['disabled']) {
            const disabled = changes['disabled'].currentValue ?? false;
            if (this.formGroup()) {
                if (disabled) {
                    this.formGroup()!.disable();
                } else {
                    this.formGroup()!.enable();
                }
            }
        }
    }

    public onCancelClick(): void {
        this.cancel.emit();
    }

    public onSubmitForm(event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        if (this.formGroup().valid) {
            // Desabilita ANTES de emitir para evitar corrida com handlers
            // que podem reabilitar o formulário em microtask de erro.
            this.formGroup().disable();
            this.submit.emit(this.formGroup().getRawValue());
        } else {
            // Marca todos os controles como touched para que os erros inline
            // (red_rectangle_icon + validation-message) apareçam junto ao
            // toast genérico, alinhando o comportamento visual de todos os
            // formulários que usam <ai-crud>.
            this.formGroup().markAllAsTouched();
            this.toastService.error('Preencha os campos obrigatórios.');
        }
    }
}
